// Consent-gated analytics/ad tag injection. Mirrors metaPixelTag.js.
// GA4 loads after analytics consent; ad tags load after marketing consent.
//
// Values are sourced from public Vite env vars. Empty string = disabled.
function readViteEnv(name) {
  try {
    const value = import.meta?.env?.[name];
    return typeof value === 'string' ? value.trim() : '';
  } catch {
    return '';
  }
}

export const GA4_MEASUREMENT_ID = readViteEnv('VITE_GA4_MEASUREMENT_ID');
export const GOOGLE_ADS_CONVERSION_ID = readViteEnv('VITE_GOOGLE_ADS_CONVERSION_ID');
export const MICROSOFT_UET_TAG_ID = readViteEnv('VITE_MICROSOFT_UET_TAG_ID');
export const REDDIT_PIXEL_ID = readViteEnv('VITE_REDDIT_PIXEL_ID');

const configuredHosts = [];
const appUrl = import.meta?.env?.VITE_APP_URL;
if (typeof appUrl === 'string') {
  try {
    configuredHosts.push(new URL(appUrl).hostname);
  } catch {
    configuredHosts.push(appUrl);
  }
}
const GA4_HOSTS = new Set([
  ...configuredHosts,
  'adulab.app',
  'www.adulab.app',
  'adu-lab-uat.web.app',
]);
const LOADED_FLAG = '__ga4_loaded';
const GOOGLE_ADS_LOADED_FLAG = '__google_ads_loaded';
const GOOGLE_TAG_BOOTED_FLAG = '__google_tag_booted';
const GOOGLE_TAG_SCRIPT_SELECTOR = 'script[data-google-tag="gtag"]';
const MICROSOFT_UET_LOADED_FLAG = '__microsoft_uet_loaded';
const MICROSOFT_UET_SCRIPT_SELECTOR = 'script[data-microsoft-uet="uet"]';
const REDDIT_PIXEL_LOADED_FLAG = '__reddit_pixel_loaded';
const REDDIT_PIXEL_SCRIPT_SELECTOR = 'script[data-reddit-pixel="pixel"]';

function isAllowedHost() {
  try {
    return GA4_HOSTS.has(window.location.hostname);
  } catch {
    return false;
  }
}

function ensureGtagShim() {
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments);
    };
  }
}

function ensureGoogleTagScript(destinationId, dataCmp) {
  if (document.querySelector(GOOGLE_TAG_SCRIPT_SELECTOR)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(destinationId)}`;
  s.setAttribute('data-cmp', dataCmp);
  s.setAttribute('data-google-tag', 'gtag');
  document.head.appendChild(s);
}

function ensureMicrosoftUetQueue() {
  window.uetq = window.uetq || [];
  return window.uetq;
}

function pushMicrosoftUetConsent(state) {
  const queue = ensureMicrosoftUetQueue();
  if (typeof queue.push === 'function') {
    queue.push('consent', 'update', { ad_storage: state });
  }
}

function ensureMicrosoftUetScript(tagId) {
  if (document.querySelector(MICROSOFT_UET_SCRIPT_SELECTOR)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = 'https://bat.bing.com/bat.js';
  s.setAttribute('data-cmp', 'microsoft-uet');
  s.setAttribute('data-microsoft-uet', 'uet');
  s.onload = () => {
    if (typeof window.UET !== 'function') return;
    const queue = ensureMicrosoftUetQueue();
    const uet = new window.UET({ ti: tagId, q: queue, enableAutoSpaTracking: true });
    window.uetq = uet;
    window.uetq.push('pageLoad');
  };
  document.head.appendChild(s);
}

function ensureRedditPixelQueue() {
  if (typeof window.rdt === 'function') return window.rdt;

  const queue = function rdt() {
    // eslint-disable-next-line prefer-rest-params
    queue.callQueue.push(Array.from(arguments));
  };
  queue.callQueue = [];
  window.rdt = queue;
  return window.rdt;
}

function ensureRedditPixelScript(pixelId) {
  if (document.querySelector(REDDIT_PIXEL_SCRIPT_SELECTOR)) return;
  const s = document.createElement('script');
  s.async = true;
  s.src = `https://www.redditstatic.com/ads/pixel.js?pixel_id=${encodeURIComponent(pixelId)}`;
  s.setAttribute('data-cmp', 'reddit-pixel');
  s.setAttribute('data-reddit-pixel', 'pixel');
  document.head.appendChild(s);
}

function configureGoogleDestination(destinationId, dataCmp, config) {
  ensureGoogleTagScript(destinationId, dataCmp);
  ensureGtagShim();
  if (!window[GOOGLE_TAG_BOOTED_FLAG]) {
    window[GOOGLE_TAG_BOOTED_FLAG] = true;
    window.gtag('js', new Date());
  }

  window.gtag('config', destinationId, config);
}

function listGoogleTagScripts() {
  const nodes = new Set();
  [
    GOOGLE_TAG_SCRIPT_SELECTOR,
    'script[data-cmp="ga4"]',
    'script[data-cmp="google-ads"]',
  ].forEach((selector) => {
    document.querySelectorAll(selector).forEach((el) => nodes.add(el));
  });
  return [...nodes];
}

function removeGoogleTagScriptIfUnused() {
  if (window[LOADED_FLAG] || window[GOOGLE_ADS_LOADED_FLAG]) return;
  window[GOOGLE_TAG_BOOTED_FLAG] = false;
  listGoogleTagScripts().forEach((el) => el.remove());
}

function expireCookies(names) {
  const expired = `; expires=${new Date(0).toUTCString()}; path=/`;
  const host = window.location?.hostname || '';

  names.forEach((name) => {
    document.cookie = `${name}=;${expired}`;
    if (!host) return;
    document.cookie = `${name}=;${expired}; domain=${host}`;
    if (!host.startsWith('.')) {
      document.cookie = `${name}=;${expired}; domain=.${host}`;
    }
  });
}

export function loadGa4() {
  if (!isAllowedHost()) return;
  if (!GA4_MEASUREMENT_ID) return;
  if (window[LOADED_FLAG]) return;
  window[LOADED_FLAG] = true;

  configureGoogleDestination(GA4_MEASUREMENT_ID, 'ga4', {
    send_page_view: true,
    anonymize_ip: true,
    allow_google_signals: true,
    allow_ad_personalization_signals: true
  });
}

export function loadGoogleAdsTag() {
  const conversionId = readViteEnv('VITE_GOOGLE_ADS_CONVERSION_ID');
  if (!isAllowedHost()) return;
  if (!conversionId) return;
  if (window[GOOGLE_ADS_LOADED_FLAG]) return;
  window[GOOGLE_ADS_LOADED_FLAG] = true;

  configureGoogleDestination(conversionId, 'google-ads', {
    send_page_view: false,
    allow_google_signals: true,
    allow_ad_personalization_signals: true
  });
}

export function loadMicrosoftUetTag() {
  const tagId = readViteEnv('VITE_MICROSOFT_UET_TAG_ID');
  if (!isAllowedHost()) return;
  if (!tagId) return;
  if (window[MICROSOFT_UET_LOADED_FLAG]) return;
  window[MICROSOFT_UET_LOADED_FLAG] = true;

  const queue = ensureMicrosoftUetQueue();
  if (typeof queue.push === 'function') {
    queue.push('consent', 'default', { ad_storage: 'denied' });
    queue.push('consent', 'update', { ad_storage: 'granted' });
  }
  ensureMicrosoftUetScript(tagId);
}

export function loadRedditPixelTag() {
  const pixelId = readViteEnv('VITE_REDDIT_PIXEL_ID');
  if (!isAllowedHost()) return;
  if (!pixelId) return;
  if (window[REDDIT_PIXEL_LOADED_FLAG]) return;
  window[REDDIT_PIXEL_LOADED_FLAG] = true;

  const rdt = ensureRedditPixelQueue();
  rdt('init', pixelId);
  rdt('track', 'PageVisit');
  ensureRedditPixelScript(pixelId);
}

export function cleanupGa4() {
  window[LOADED_FLAG] = false;
  const gaCookies = ['_ga', '_gid'];
  if (GA4_MEASUREMENT_ID) {
    gaCookies.push(`_ga_${GA4_MEASUREMENT_ID.replace('G-', '')}`);
  }

  expireCookies(gaCookies);
  removeGoogleTagScriptIfUnused();
}

export function cleanupGoogleAdsTag() {
  window[GOOGLE_ADS_LOADED_FLAG] = false;
  expireCookies(['_gcl_au', '_gcl_aw', '_gcl_dc', '_gcl_gs', '_gcl_ls']);
  removeGoogleTagScriptIfUnused();
}

export function cleanupMicrosoftUetTag() {
  window[MICROSOFT_UET_LOADED_FLAG] = false;
  try {
    pushMicrosoftUetConsent('denied');
    window.uetq = ['consent', 'default', { ad_storage: 'denied' }];
  } catch {
    /* non-fatal */
  }
  expireCookies(['_uetsid', '_uetvid', '_uetmsclkid']);
  document.querySelectorAll(MICROSOFT_UET_SCRIPT_SELECTOR).forEach((el) => el.remove());
}

export function cleanupRedditPixelTag() {
  window[REDDIT_PIXEL_LOADED_FLAG] = false;
  expireCookies(['_rdt_uuid', 'rdt_uuid']);
  document.querySelectorAll(REDDIT_PIXEL_SCRIPT_SELECTOR).forEach((el) => el.remove());
  try {
    delete window.rdt;
  } catch {
    window.rdt = undefined;
  }
}
